import * as t from 'io-ts';
import type { Context } from 'io-ts-to-json-schema';
import { toJsonSchema } from 'io-ts-to-json-schema';
import { omit, VegaSpec, PipeDocument, Signal } from '@bayer-int/portletce-shared';
import { cloneDeepWith, mapValues } from 'lodash-es';

import { Specification } from './specification';
import type {
    AcSpecId} from './view';
import {
    AcDocSpecIdC,
    AcSourceSpecIdC,
    AcTransformSpecIdC,
    UtilitySpecIdC,
    View,
} from './view';
import { FilterDataInput } from '.';
import { SettingSpecValueCodecs } from './settings/settingDefinitions';
import { Settings } from './settings';
import { CustomSettingFormDefinitionInput } from './settings/customSettingFormDefinitionInput';

export const MainConfigC = t.intersection([
    t.type(omit('views', Specification.requiredCodec.props)),
    Specification.optionalCodec,
]);

const ViewC = t.union([View.codec, View.withRefCodec]);

const TestingC = t.type({
    settings: Specification.requiredCodec.props.settings,
    filters: t.array(FilterDataInput.codec),
    signals: t.array(Signal.codec),
});

type SpecIds = {
    source: AcSpecId[];
    transforms: AcSpecId[];
    document: AcSpecId[];
    utility: AcSpecId[];
};

/**
 * Following schemas can be used in any text editor that supports json-schema
 */
export const PortletSchemas = {
    mainConfig: () => toJsonSchema(MainConfigC, commonOptions()),
    view: (specIds: SpecIds) => toJsonSchema(ViewC, commonOptions({ specIds })),
    spec: () => toJsonSchema(VegaSpec.codec, commonOptions()),
    document: () => toJsonSchema(PipeDocument.codec, commonOptions()),
    testing: () => toJsonSchema(TestingC, commonOptions()),
};

export type AdditionalSchemaData = { specIds: SpecIds };

function commonOptions(additionalData?: AdditionalSchemaData): Partial<Context> {
    const codecModifiers: Record<string, (c: t.Mixed) => t.Mixed> = {
        /**
         * Specs and documents in aclibv2 under the hood require the `id` property,
         * but it should not be validated when text is checked against schema
         */
        UserMetaRequired: codec => {
            if (!(codec instanceof t.InterfaceType)) return codec;
            return t.type(omit('id', codec.props), codec.name);
        },
    };

    const schemaModifiers: Record<string, Context['customizer']> = {
        /**
         * Aclibv2 specs only validate the `usermeta` section, but under the hood they are
         * vega configurations - this allows for better intellisense
         */
        VegaSpecification: schema => ({
            allOf: [schema, { $ref: 'https://vega.github.io/schema/vega/v5.json' }],
        }),

        /**
         * As "scale" is a numeric value, it's default is 0
         * but such value for scale is useless
         */
        [SettingSpecValueCodecs.SCALE.name]: (schema, _codec, _context) => {
            return cloneDeepWith(schema, subSchema =>
                t.type({ type: t.literal('number') }).is(subSchema)
                    ? { ...subSchema, default: 1 }
                    : undefined,
            );
        },

        /**
         * Following two schema modifiers improve/fix intellisense for custom settings,
         * which have big overlap with setting object notation (value and scope are common properties).
         *
         * First of all, we extend custom setting record schema with
         * `properties: Record<KNOWN_SETTING_FIELDS, {}>`, so that schema defined via
         * `additionalProperties` does not apply to known settings.
         *
         * Secondly, we disallow providing plain and object notation setting values for custom settings.
         * It is required for TS when intersecting record with index signature, but breaks the intellisense.
         * See comment near `CustomSettingsValueC` in `settings/index.ts`
         */
        [Settings.customSettingsCodec.name]: (schema, _codec, _context) => {
            return t.type({ type: t.literal('object') }).is(schema)
                ? {
                      ...schema,
                      properties: mapValues(Settings.knownSettingsCodec.props, () => ({})),
                  }
                : schema;
        },
        [Settings.customSettingsValueCodec.name]: (schema, _codec, _context) => {
            return t.type({ anyOf: t.array(t.partial({ description: t.string })) }).is(schema)
                ? {
                      ...schema,
                      anyOf: schema.anyOf.filter(
                          ({ description }) =>
                              description === CustomSettingFormDefinitionInput.codec.name,
                      ),
                  }
                : schema;
        },

        ...(additionalData?.specIds && {
            [UtilitySpecIdC.name]: (_schema, _codec, _context) => ({
                anyOf: additionalData.specIds.utility.map(id => ({
                    const: id,
                    description: id,
                })),
            }),
            [AcSourceSpecIdC.name]: (_schema, _codec, _context) => ({
                anyOf: additionalData.specIds.source.map(id => ({
                    const: id,
                    description: id,
                })),
            }),
            [AcTransformSpecIdC.name]: (_schema, _codec, _context) => ({
                anyOf: additionalData.specIds.transforms.map(id => ({
                    const: id,
                    description: id,
                })),
            }),
            [AcDocSpecIdC.name]: (_schema, _codec, _context) => ({
                anyOf: additionalData.specIds.document.map(id => ({
                    const: id,
                    description: id,
                })),
            }),
        }),
    };

    return {
        codecCustomizer: codec =>
            codecModifiers[codec.name] ? codecModifiers[codec.name](codec) : codec,

        customizer: (schema, codec, context) =>
            schemaModifiers[codec.name]
                ? schemaModifiers[codec.name](schema, codec, context)
                : schema,
    };
}
